import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "How it Works", to: "/how-it-works" },
  { label: "Services", to: "/services" },
  { label: "Amazon Overstock", to: "/amazon-overstock" },
  { label: "Submit Inventory", to: "/submit-inventory" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState("EN");
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white shadow-[0_4px_24px_rgba(27,37,69,0.08)]" : "bg-white/90 backdrop-blur"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3.5 lg:px-8">
        <Link to="/" aria-label="Home" className="shrink-0">
          <Logo dense />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-5 xl:flex">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`nav-link whitespace-nowrap text-[13.5px] font-medium text-navy-brand ${
                location.pathname === item.to ? "active" : ""
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <div className="flex items-center gap-1 text-sm text-navy-brand">
            <Globe size={16} className="text-teal-brand" />
            {["EN", "ES"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-1 font-semibold transition-colors ${
                  lang === l ? "text-teal-brand" : "text-slate-400 hover:text-navy-brand"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Link
            to="/submit-inventory"
            className="whitespace-nowrap rounded-full bg-navy-brand px-5 py-2.5 text-[13.5px] font-semibold text-white transition-all duration-300 hover:bg-teal-brand hover:shadow-lg"
          >
            Submit Inventory
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="xl:hidden text-navy-brand"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-slate-100 bg-white px-5 py-4 xl:hidden">
          <nav className="flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-lg px-3 py-2.5 text-base font-medium transition-colors ${
                  location.pathname === item.to
                    ? "bg-teal-brand/10 text-teal-brand"
                    : "text-navy-brand hover:bg-slate-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/submit-inventory"
              className="mt-3 rounded-full bg-navy-brand px-6 py-3 text-center text-sm font-semibold text-white"
            >
              Submit Inventory
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
